import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { dashboard, isForbidden } from '@cwos/api-client';
import type { dashboard as DashboardNS } from '@cwos/api-client';
import { Refused, SkeletonSet } from '@cwos/ui-admin';
import { useAnalyticsControls } from '../AnalyticsContext.js';
import { askPeriod, formatMetricValue, formatNumber, periodRangeLabel } from '../analyticsRange.js';
import { DrillPanel, WeeklyPanel, type Drill, TrendFailure } from '../drilldown.js';
import { HEADLINE_ABOUT, METRIC_ABOUT } from '../explain.js';
import { KpiCard, StalenessStrip, type QueryLike } from '../shared.js';
import { LoadFailure } from '../../../LoadFailure.js';
import { explainDraft } from '../../../intelligence/global/index.js';

/**
 * What each headline opens to. The delivered-population figures open their
 * visits; the roster and the time clock have no visit rows, so they open the
 * Staff view, where their rows are.
 */
const KPI_RECORDS: Partial<Record<DashboardNS.HeadlineMetricKey, true>> = {
  appointments: true,
  serviceHours: true,
  clientsServed: true,
  workersDelivering: true,
};

/** A headline's comparison, in words — the server's prior, never re-derived. */
function priorSentence(m: DashboardNS.HeadlineMetric): string | null {
  if (m.current == null || m.prior == null) return null;
  const fmt = (v: number) => formatMetricValue(v, m.unit, m.precision);
  if (m.changePct == null || m.direction === 'flat') {
    return `${fmt(m.current)}, unchanged from ${fmt(m.prior)} in the previous period.`;
  }
  return `${fmt(m.current)}, ${m.direction} ${Math.abs(m.changePct)}% from ${fmt(m.prior)} in the previous period.`;
}

export function OverviewView() {
  const { apiQuery, setView } = useAnalyticsControls();
  const [drill, setDrill] = useState<Drill | null>(null);

  const overview = useQuery({
    queryKey: ['analytics', 'overview', apiQuery],
    queryFn: () => dashboard.analyticsOverview(apiQuery),
  });

  // THE SAME PERIOD AS THE HEADLINES. This used to send only the filters, so
  // the charts drew a fixed 26 weeks beside headline figures for "this week".
  const trends = useQuery({
    queryKey: ['analytics', 'trends', apiQuery],
    queryFn: () => dashboard.analyticsTrends(apiQuery),
    refetchInterval: 60_000,
  });

  if (overview.isError && isForbidden(overview.error)) {
    return (
      <Refused
        heading="The overview is not shown to you"
        message="Your account does not have access to these figures. Nothing has been left out of them — they are not yours to see."
      />
    );
  }
  if (overview.isLoading) return <SkeletonSet surface="table" rows={4} />;
  if (overview.isError && !overview.data) {
    return (
      <LoadFailure
        error={overview.error}
        message="We couldn’t load the overview just now, so read this screen as unknown rather than empty."
        onRetry={() => void overview.refetch()}
      />
    );
  }

  const data = overview.data;
  // `return null` rendered a blank screen with no reason given.
  if (!data) return <SkeletonSet surface="table" rows={4} />;

  const openMetric = (m: DashboardNS.HeadlineMetric) => {
    const about = METRIC_ABOUT[HEADLINE_ABOUT[m.key]];
    const span = { from: data.period.from, to: data.period.to };
    setDrill({
      title: `${m.label}, ${periodRangeLabel(data.period)}`,
      about,
      change: priorSentence(m),
      attention: m.unavailableMessage ?? null,
      ask: explainDraft(m.label.toLowerCase(), askPeriod(data.period)),
      ...(KPI_RECORDS[m.key]
        ? { records: { set: 'delivered' as const, scope: data.scope, ...span } }
        : { elsewhere: { label: 'Open the Staff view', onOpen: () => setView('staff') } }),
    });
  };

  return (
    <div className="an-stack">
      {/* An empty `metrics` array rendered an empty `<div>` — the whole KPI row
          simply vanished with nothing to say it had. A site-scoped caller gets
          exactly that shape, because `filterScope` returns no accessible
          programs for a view with no site dimension. */}
      {data.metrics.length === 0 ? (
        <p className="rn-muted" role="status">
          No headline figures came back for this period and these filters. If your access is limited
          to particular sites, this view cannot honour that and will report nothing rather than the
          organization.
        </p>
      ) : null}
      <div className="an-kpis">
        {data.metrics.map((m) => (
          <KpiCard
            key={m.key}
            metric={m}
            // The envelope travels WITH the metric, so the card can name the
            // floor in its own sentence rather than the screen guessing it.
            disclosure={data.disclosure}
            // THE RESOLVED WINDOW, ON THE CARD ITSELF — not only once above the
            // whole KPI row. See `KpiCard`'s own doc comment for why a card
            // needs its own copy of the period it was computed over.
            period={data.period}
            onClick={() => openMetric(m)}
          />
        ))}
      </div>

      {drill ? <DrillPanel key={drill.title} drill={drill} onClose={() => setDrill(null)} /> : null}

      {data.staleness ? <StalenessStrip staleness={data.staleness} /> : null}

      <TrendPanel trends={trends} onDrill={setDrill} />
    </div>
  );
}

const hours = (v: number) => formatNumber(v, Number.isInteger(v) ? 0 : 1);

function TrendPanel({
  trends,
  onDrill,
}: {
  trends: QueryLike<DashboardNS.AnalyticsTrends>;
  onDrill: (drill: Drill) => void;
}) {
  if (trends.isLoading) return <SkeletonSet surface="band" />;
  if (trends.isError && !trends.data) {
    return (
      <TrendFailure
        error={trends.error}
        message="We couldn’t load the trend charts just now."
        onRetry={() => void trends.refetch()}
      />
    );
  }
  const d = trends.data;
  if (!d) return null;
  const s = d.series;

  return (
    <div className="an-grid">
      <WeeklyPanel
        title="Services delivered"
        axis={d}
        about={METRIC_ABOUT.servicesDelivered}
        additive
        onDrill={onDrill}
        lines={[
          {
            name: 'Services delivered',
            color: 'var(--chart-2)',
            series: s.appointments,
            set: 'delivered',
          },
        ]}
      />
      <WeeklyPanel
        title="Service hours delivered"
        axis={d}
        about={METRIC_ABOUT.serviceHours}
        additive
        formatValue={hours}
        onDrill={onDrill}
        lines={[
          {
            name: 'Service hours delivered',
            color: 'var(--chart-1)',
            series: s.serviceHours,
            set: 'delivered',
          },
        ]}
      />
      <WeeklyPanel
        title="Workers delivering service"
        axis={d}
        about={METRIC_ABOUT.workersDelivering}
        onDrill={onDrill}
        lines={[
          {
            name: 'Workers delivering service',
            color: 'var(--chart-4)',
            series: s.workersDelivering,
            set: 'delivered',
          },
        ]}
      />
      <WeeklyPanel
        title="Clients served"
        axis={d}
        about={METRIC_ABOUT.clientsServed}
        onDrill={onDrill}
        lines={[
          {
            name: 'Clients served',
            color: 'var(--chart-3)',
            series: s.clientsServed,
            set: 'delivered',
          },
        ]}
      />
      <WeeklyPanel
        title="Hours worked (time clock)"
        axis={d}
        about={METRIC_ABOUT.hoursWorked}
        additive
        formatValue={hours}
        lines={[{ name: 'Hours worked', color: 'var(--chart-1)', series: s.hours }]}
      />
    </div>
  );
}
